import { Button, Input, type ToastType } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { Company } from "../../../api/addressBook.service";
import {
  userManagement,
  type CreateOrganisationPayload,
} from "../../../api/userManagement.service";
import {
  extractZodIssues,
  mapZodIssuesToFieldErrors,
} from "../../../api/profiles/validationErrors";
import { CompanyPicker } from "../../../components/CompanyPicker/CompanyPicker";
import { useMutation } from "../../../hooks/useApi.js";
import { ROUTES } from "../../../routes";
import styles from "../UserManagement.module.css";

type OrgLayoutContext = {
  setToast: (toast: { message: string; type: ToastType } | null) => void;
};

// Show the WordPress company id so staff can tell apart same-named firms (e.g. several "Dentons").
function companyIdAnnotation(company: Company): string | null {
  return company.companyId != null ? `(companyId: ${company.companyId})` : null;
}

function OrganisationNew() {
  const navigate = useNavigate();
  const { setToast } = useOutletContext<OrgLayoutContext>();
  const { mutate: createOrganisation } = useMutation<
    Awaited<ReturnType<typeof userManagement.createOrganisation>>,
    [CreateOrganisationPayload]
  >(userManagement.createOrganisation);

  const [name, setName] = useState("");
  const [company, setCompany] = useState<Company | null>(null);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [companyError, setCompanyError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isSubmitting) return;

    setNameError(undefined);
    setCompanyError(undefined);

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setNameError("Name is required");
      return;
    }

    const payload: CreateOrganisationPayload = { name: trimmedName };
    if (company) {
      payload.companyId = company.id;
    }

    setIsSubmitting(true);
    try {
      const created = await createOrganisation(payload);
      setToast({ message: "Organisation created", type: "success" });
      navigate(ROUTES.users.organisationDetail(created.id));
    } catch (err) {
      const issues = extractZodIssues(err);
      if (issues) {
        const { fieldErrors, orphanMessages } = mapZodIssuesToFieldErrors(issues, [
          "name",
          "companyId",
        ] as const);
        if (fieldErrors.name) {
          setNameError(fieldErrors.name);
        }
        if (fieldErrors.companyId) {
          setCompanyError(fieldErrors.companyId);
        }
        if (orphanMessages.length > 0) {
          setToast({ message: orphanMessages.join("\n"), type: "error" });
        }
      } else {
        setToast({
          message: err instanceof Error ? err.message : "Failed to create organisation",
          type: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={styles.detailWrapper}>
      <h1 className={styles.contextTitle}>Create organisation</h1>

      <form onSubmit={handleSubmit} className={styles.section}>
        <Input
          label="Name"
          value={name}
          onChange={event => {
            setName(event.target.value);
            setNameError(undefined);
          }}
          error={nameError}
          disabled={isSubmitting}
          required
          fullWidth
          autoComplete="off"
        />

        <CompanyPicker
          label="Company"
          value={company}
          onChange={value => {
            setCompany(value);
            setCompanyError(undefined);
          }}
          error={companyError}
          disabled={isSubmitting}
          renderOptionAnnotation={companyIdAnnotation}
        />

        <div className={styles.formActions}>
          <Button
            variant="outlined"
            color="default"
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create organisation"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default OrganisationNew;
